/**
 * Confirmation dialog for merging 2+ selected entities: pick which one
 * survives (a radio list), the rest are merged into it. Returns the chosen
 * target id, or null if cancelled.
 */
export interface MergeCandidate {
  id: string;
  name: string;
}

export class MergeDialog {
  private candidates: MergeCandidate[];
  private entityLabel: string;
  private modalElement: HTMLElement | null = null;
  private resolvePromise: ((targetId: string | null) => void) | null = null;

  constructor(candidates: MergeCandidate[], entityLabel: string) {
    this.candidates = candidates;
    this.entityLabel = entityLabel;
  }

  async open(): Promise<string | null> {
    return new Promise((resolve) => {
      this.resolvePromise = resolve;
      this.createModal();
    });
  }

  private createModal(): void {
    this.modalElement = document.createElement('div');
    this.modalElement.className = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50';

    const content = document.createElement('div');
    content.className = 'bg-[#1e1e2e] rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl border border-[var(--accent-alpha-20)]';

    const header = document.createElement('h2');
    header.className = 'text-xl font-semibold mb-2 text-[var(--accent)] font-mono';
    header.textContent = `Fusiona ${this.entityLabel}`;
    content.appendChild(header);

    const message = document.createElement('p');
    message.className = 'text-[#a0a0a0] text-sm mb-3';
    message.textContent = 'Quin ha de sobreviure? Les imatges i totes les referències dels altres es traslladaran a aquest, i la resta s\'esborraran.';
    content.appendChild(message);

    const radioGroup = document.createElement('div');
    radioGroup.className = 'space-y-2 mb-4 max-h-64 overflow-y-auto';
    let selectedId: string | null = this.candidates[0]?.id ?? null;

    for (const candidate of this.candidates) {
      const label = document.createElement('label');
      label.className = 'flex items-center gap-2 text-sm text-[#e0e0e0] font-mono cursor-pointer';

      const radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = 'merge-target';
      radio.value = candidate.id;
      radio.checked = candidate.id === selectedId;
      radio.addEventListener('change', () => { selectedId = candidate.id; });
      label.appendChild(radio);

      const name = document.createElement('span');
      name.textContent = candidate.name;
      label.appendChild(name);

      radioGroup.appendChild(label);
    }
    content.appendChild(radioGroup);

    const buttonGroup = document.createElement('div');
    buttonGroup.className = 'flex gap-3';

    const confirmBtn = document.createElement('button');
    confirmBtn.className = 'flex-1 bg-[var(--accent)] text-[#0d1117] font-bold px-4 py-2 rounded-md hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[var(--accent)] font-mono';
    confirmBtn.textContent = 'Fusiona';
    confirmBtn.addEventListener('click', () => this.close(selectedId));
    buttonGroup.appendChild(confirmBtn);

    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'flex-1 border border-[var(--accent-alpha-20)] text-[#a0a0a0] px-4 py-2 rounded-md hover:bg-[#0d1117] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] font-mono';
    cancelBtn.textContent = 'Cancel·la';
    cancelBtn.addEventListener('click', () => this.close(null));
    buttonGroup.appendChild(cancelBtn);

    content.appendChild(buttonGroup);
    this.modalElement.appendChild(content);
    document.body.appendChild(this.modalElement);
  }

  private close(targetId: string | null): void {
    this.modalElement?.remove();
    this.modalElement = null;
    this.resolvePromise?.(targetId);
    this.resolvePromise = null;
  }
}
